import { skipToken, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { cdrLabReportHeaderSchema } from '@/components/patient-card/patient-card-schema';
import { cdrApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/cdr/api-schema';
import { labReportDetailSchema } from './lab-report-detail-schema';

const API_PATH = '/v1/labReport/queryLabReport/{reportId}' as const;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const responseSchema = cdrLabReportHeaderSchema.extend({
  labReportDetails: z.array(labReportDetailSchema),
});

type LabReportByIdData = z.infer<typeof responseSchema>;

const queryFn = async (reportId: string, signal: AbortSignal) => {
  const { data: response } = await cdrApi.get<APIResponse>(
    API_PATH.replace('{reportId}', reportId),
    { signal },
  );

  return responseSchema.parse(response.data);
};

export const useQueryLabReportById = (
  reportId?: string,
  options?: QueryOptions<LabReportByIdData>,
) => {
  return useQuery({
    queryKey: [API_PATH, reportId],
    queryFn: reportId ? ({ signal }) => queryFn(reportId, signal) : skipToken,
    ...options,
  });
};
